import ApplicationController from './application_controller';
import autocomplete from 'autocomplete.js';
import { escapeAutocompleteHtml } from '../utils/autocomplete';

const normalize = (value) => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

export default class extends ApplicationController {
  static targets = [
    'originInput', 'originId', 'originLabel',
    'destinationInput', 'destinationId', 'destinationLabel', 'destinationPath',
    'status', 'submit',
  ];
  static values = {
    url: String,
    blankText: String,
    errorMessage: String,
    labelTooLongText: String,
    maxLabelLength: Number,
    allPlaces: Array,
    originPlaces: Array,
    destinationPlaces: Array,
    optionalPlaces: Boolean,
  };

  connect() {
    this.requestVersion = (this.requestVersion || 0) + 1;
    this.originPlaces = this.originPlacesValue;
    this.destinationPlaces = this.destinationPlacesValue;
    this.autocompletes = new Map();
    this.generatedPath = this.hasDestinationPathTarget
      && this.destinationPathTarget.value === this.routePath() ? this.destinationPathTarget.value : '';
    this.addAutocomplete('origin');
    this.addAutocomplete('destination');
  }

  disconnect() {
    this.requestVersion += 1;
    this.autocompletes.forEach((binding) => {
      binding.input.removeEventListener('input', binding.inputHandler);
      binding.input.removeEventListener('keydown', binding.keydownHandler);
      binding.autocomplete.autocomplete.destroy();
    });
    this.autocompletes = new Map();
  }

  originChanged() {
    this.beginChange();
    const originSlug = this.selectedSlug('origin');
    if (originSlug) return this.loadPlaces('from', originSlug, 'destination');

    const destinationSlug = this.selectedSlug('destination');
    if (destinationSlug) return this.loadPlaces('to', destinationSlug, 'origin');

    this.restoreAllPlaces();
    return Promise.resolve();
  }

  destinationChanged() {
    this.beginChange();
    const destinationSlug = this.selectedSlug('destination');
    if (destinationSlug) return this.loadPlaces('to', destinationSlug, 'origin');

    const originSlug = this.selectedSlug('origin');
    if (originSlug) return this.loadPlaces('from', originSlug, 'destination');

    this.restoreAllPlaces();
    return Promise.resolve();
  }

  beginChange() {
    this.requestVersion += 1;
    this.originInputTarget.disabled = false;
    this.destinationInputTarget.disabled = false;
    this.submitTarget.disabled = false;
  }

  async loadPlaces(direction, slug, targetName) {
    const requestVersion = this.requestVersion;
    const inputTarget = this[`${targetName}InputTarget`];
    const idTarget = this[`${targetName}IdTarget`];
    const selectedValue = String(idTarget.value);
    inputTarget.disabled = true;
    this.submitTarget.disabled = true;

    try {
      const response = await fetch(`${this.urlValue}?${direction}=${encodeURIComponent(slug)}`, {
        headers: { Accept: 'application/json' },
      });
      if (response.redirected) {
        window.location.assign(response.url);
        return;
      }
      if (!response.ok) throw new Error(`Place lookup failed with status ${response.status}`);

      const places = await response.json();
      if (requestVersion !== this.requestVersion) return;

      this[`${targetName}Places`] = places;
      this.reconcileSelection(targetName, places, selectedValue);
      this.statusTarget.textContent = '';
    } catch (_error) {
      if (requestVersion !== this.requestVersion) return;
      this.clearSelection(targetName);
      this.statusTarget.textContent = this.errorMessageValue;
    } finally {
      if (requestVersion === this.requestVersion) {
        inputTarget.disabled = false;
        this.submitTarget.disabled = false;
      }
    }
  }

  submit(event) {
    if (this.submitTarget.disabled) {
      event.preventDefault();
      return;
    }

    const requiresPlaces = !this.optionalPlacesValue
      || this.originIdTarget.value || this.destinationIdTarget.value;
    const invalidPlace = requiresPlaces && ['origin', 'destination'].find((targetName) => {
      const inputTarget = this[`${targetName}InputTarget`];
      const idTarget = this[`${targetName}IdTarget`];
      inputTarget.setCustomValidity(idTarget.value ? '' : this.blankTextValue);
      return !idTarget.value;
    });
    const labelValidity = ['origin', 'destination'].filter(
      (targetName) => this[`has${targetName[0].toUpperCase()}${targetName.slice(1)}LabelTarget`],
    ).map((targetName) => ({
      targetName,
      valid: this.validateLabelInput(this[`${targetName}LabelTarget`]),
    }));
    const invalidLabel = labelValidity.find(({ valid }) => !valid)?.targetName;
    const invalidInput = invalidPlace
      ? this[`${invalidPlace}InputTarget`]
      : invalidLabel && this[`${invalidLabel}LabelTarget`];

    if (invalidInput) {
      event.preventDefault();
      invalidInput.reportValidity();
    }
  }

  labelChanged(event) {
    this.validateLabelInput(event.currentTarget);
  }

  validateLabelInput(input) {
    const exceedsLimit = Array.from(input.value.trim()).length > this.maxLabelLengthValue;
    input.setCustomValidity(exceedsLimit ? this.labelTooLongTextValue : '');
    return !exceedsLimit;
  }

  addAutocomplete(targetName) {
    const inputTarget = this[`${targetName}InputTarget`];
    const idTarget = this[`${targetName}IdTarget`];
    const ac = autocomplete(
      inputTarget,
      { hint: false, autoselect: true, minLength: 0, openOnFocus: true },
      [
        {
          source: this.source(targetName),
          debounce: 200,
          cache: false,
          templates: {
            suggestion: (suggestion) => escapeAutocompleteHtml(suggestion.name),
          },
        },
      ],
    ).on('autocomplete:selected', (_event, suggestion) => {
      ac.autocomplete.setVal(suggestion.name);
      idTarget.value = String(suggestion.id);
      inputTarget.setCustomValidity('');
      this.syncDestinationPath();
      return this[`${targetName}Changed`]();
    });

    const inputHandler = () => this.selectionEdited(targetName);
    const keydownHandler = this.keydown.bind(this);
    inputTarget.addEventListener('input', inputHandler);
    inputTarget.addEventListener('keydown', keydownHandler);
    this.autocompletes.set(targetName, {
      autocomplete: ac, input: inputTarget, inputHandler, keydownHandler,
    });
  }

  source(targetName) {
    return (query, callback) => {
      const normalizedQuery = normalize(query);
      if (!normalizedQuery) {
        callback(this[`${targetName}Places`]);
        return;
      }

      callback(this[`${targetName}Places`].filter(
        (place) => normalize(place.name).includes(normalizedQuery),
      ));
    };
  }

  selectionEdited(targetName) {
    this.beginChange();
    const idTarget = this[`${targetName}IdTarget`];
    const inputTarget = this[`${targetName}InputTarget`];
    idTarget.value = '';
    inputTarget.setCustomValidity('');
    this.syncDestinationPath();

    if (!this.originIdTarget.value && !this.destinationIdTarget.value) this.restoreAllPlaces();
  }

  keydown(event) {
    if (event.key === 'Enter') event.preventDefault();
  }

  selectedSlug(targetName) {
    const selectedId = String(this[`${targetName}IdTarget`].value);
    return this[`${targetName}Places`].find((place) => String(place.id) === selectedId)?.slug || '';
  }

  routePath() {
    const origin = this.selectedSlug('origin');
    const destination = this.selectedSlug('destination');
    return origin && destination
      ? `/search/${encodeURIComponent(origin)}/${encodeURIComponent(destination)}` : '';
  }

  syncDestinationPath() {
    if (!this.hasDestinationPathTarget) return;
    const path = this.routePath();
    if (path) this.destinationPathTarget.value = path;
    else if (this.destinationPathTarget.value === this.generatedPath) this.destinationPathTarget.value = '';
    this.generatedPath = path;
  }

  restoreAllPlaces() {
    this.originPlaces = this.allPlacesValue;
    this.destinationPlaces = this.allPlacesValue;
    this.statusTarget.textContent = '';
  }

  reconcileSelection(targetName, places, selectedValue) {
    const selectedPlace = places.find((place) => String(place.id) === selectedValue);
    if (!selectedPlace) {
      this.clearSelection(targetName);
      return;
    }

    this.setInputValue(targetName, selectedPlace.name);
    this.syncDestinationPath();
  }

  clearSelection(targetName) {
    const inputTarget = this[`${targetName}InputTarget`];
    const idTarget = this[`${targetName}IdTarget`];
    this.setInputValue(targetName, '');
    inputTarget.setCustomValidity('');
    idTarget.value = '';
    this.syncDestinationPath();
  }

  setInputValue(targetName, value) {
    this.autocompletes.get(targetName).autocomplete.autocomplete.setVal(value);
  }
}
